/**
 * AllToolsModal - Modal für Tool-Verwaltung
 *
 * Ermöglicht das Verwalten von:
 * - Sortierung (Drag & Drop)
 * - Favoriten (Star-Icon)
 * - Sichtbarkeit (Eye-Icon)
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Star, Eye, EyeOff, RotateCcw, GripVertical } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@/components/v2/ui';
import { cn } from '@/lib/utils';
import { useNavigationPreferences, type WorkspaceToolWithIcon } from '@/hooks/useNavigationPreferences';

interface AllToolsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AllToolsModal({ open, onOpenChange }: AllToolsModalProps) {
  const { t } = useTranslation();
  const {
    organizedTools,
    isFavorite,
    isHidden,
    toggleFavorite,
    toggleVisibility,
    updateSortOrder,
    reset,
    isUpdating,
  } = useNavigationPreferences();

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      // Find which section each item belongs to
      const findSection = (id: string) => {
        if (organizedTools.favorites.some((t) => t.id === id)) return 'favorites';
        if (organizedTools.normal.some((t) => t.id === id)) return 'normal';
        if (organizedTools.hidden.some((t) => t.id === id)) return 'hidden';
        return null;
      };

      const activeSection = findSection(active.id as string);
      const overSection = findSection(over.id as string);

      // Only allow sorting within the same section
      if (!activeSection || activeSection !== overSection) return;

      // Get the tools in the active section
      const sectionTools =
        activeSection === 'favorites'
          ? organizedTools.favorites
          : activeSection === 'normal'
            ? organizedTools.normal
            : organizedTools.hidden;

      const oldIndex = sectionTools.findIndex((t) => t.id === active.id);
      const newIndex = sectionTools.findIndex((t) => t.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return;

      // Reorder within section and recalculate global sort order
      const reordered = [...sectionTools];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      // Calculate new global sort order (preserve other sections)
      const allTools = [
        ...(activeSection === 'favorites' ? reordered : organizedTools.favorites),
        ...(activeSection === 'normal' ? reordered : organizedTools.normal),
        ...(activeSection === 'hidden' ? reordered : organizedTools.hidden),
      ];

      const newOrder: Record<string, number> = {};
      allTools.forEach((tool, index) => {
        newOrder[tool.id] = index;
      });

      updateSortOrder(newOrder);
    },
    [organizedTools, updateSortOrder]
  );

  const handleToggleFavorite = (tool: WorkspaceToolWithIcon) => {
    toggleFavorite(tool.id, !isFavorite(tool.id));
  };

  const handleToggleVisibility = (tool: WorkspaceToolWithIcon) => {
    toggleVisibility(tool.id, !isHidden(tool.id));
  };

  const handleReset = () => {
    reset();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('tools.allToolsTitle')}</DialogTitle>
          <DialogDescription>
            Verwalte deine Favoriten und ausgeblendete Tools
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6">
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <div className="space-y-6">
              {/* Favorites Section */}
              {organizedTools.favorites.length > 0 && (
                <ToolSection
                  title={t('tools.favorites')}
                  icon={Star}
                  tools={organizedTools.favorites}
                  onToggleFavorite={handleToggleFavorite}
                  onToggleVisibility={handleToggleVisibility}
                  isUpdating={isUpdating}
                  isFavorite={isFavorite}
                  isHidden={isHidden}
                />
              )}

              {/* Normal Tools Section */}
              {organizedTools.normal.length > 0 && (
                <ToolSection
                  title={t('tools.toolsLabel')}
                  tools={organizedTools.normal}
                  onToggleFavorite={handleToggleFavorite}
                  onToggleVisibility={handleToggleVisibility}
                  isUpdating={isUpdating}
                  isFavorite={isFavorite}
                  isHidden={isHidden}
                />
              )}

              {/* Hidden Tools Section */}
              {organizedTools.hidden.length > 0 && (
                <ToolSection
                  title={t('tools.hidden')}
                  icon={EyeOff}
                  tools={organizedTools.hidden}
                  onToggleFavorite={handleToggleFavorite}
                  onToggleVisibility={handleToggleVisibility}
                  isUpdating={isUpdating}
                  isFavorite={isFavorite}
                  isHidden={isHidden}
                />
              )}
            </div>
          </DndContext>
        </div>

        <DialogFooter className="border-t pt-4 -mx-6 px-6">
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={isUpdating}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Zurücksetzen
          </Button>
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            Fertig
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ToolSectionProps {
  title: string;
  icon?: typeof Star;
  tools: WorkspaceToolWithIcon[];
  onToggleFavorite: (tool: WorkspaceToolWithIcon) => void;
  onToggleVisibility: (tool: WorkspaceToolWithIcon) => void;
  isUpdating: boolean;
  isFavorite: (toolId: string) => boolean;
  isHidden: (toolId: string) => boolean;
}

function ToolSection({
  title,
  icon: Icon,
  tools,
  onToggleFavorite,
  onToggleVisibility,
  isUpdating,
  isFavorite,
  isHidden,
}: ToolSectionProps) {
  return (
    <div>
      {/* Section Header */}
      <div className="mb-3 px-3 flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-amber-400" />}
        <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
          {title}
        </span>
        <span className="text-xs text-neutral-400 dark:text-neutral-500">
          ({tools.length})
        </span>
      </div>

      {/* Sortable Tools */}
      <SortableContext items={tools.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1">
          {tools.map((tool) => (
            <SortableToolRow
              key={tool.id}
              tool={tool}
              isFavorite={isFavorite(tool.id)}
              isHidden={isHidden(tool.id)}
              onToggleFavorite={() => onToggleFavorite(tool)}
              onToggleVisibility={() => onToggleVisibility(tool)}
              disabled={isUpdating}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

interface ToolRowProps {
  tool: WorkspaceToolWithIcon;
  isFavorite: boolean;
  isHidden: boolean;
  onToggleFavorite: () => void;
  onToggleVisibility: () => void;
  disabled?: boolean;
}

function SortableToolRow(props: ToolRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.tool.id,
    // @ts-expect-error - activationConstraint is valid but not in current type definitions
    activationConstraint: { distance: 5 },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(isDragging && 'z-50 opacity-80')}
    >
      <ToolRow {...props} dragHandleProps={{ listeners, attributes }} />
    </div>
  );
}

interface DragHandleProps {
  listeners?: ReturnType<typeof useSortable>['listeners'];
  attributes?: ReturnType<typeof useSortable>['attributes'];
}

function ToolRow({
  tool,
  isFavorite,
  isHidden,
  onToggleFavorite,
  onToggleVisibility,
  disabled,
  dragHandleProps,
}: ToolRowProps & { dragHandleProps?: DragHandleProps }) {
  const { t } = useTranslation();
  const Icon = tool.icon;

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg',
        'hover:bg-neutral-100 dark:hover:bg-neutral-800',
        isHidden && 'opacity-50'
      )}
    >
      {/* Drag Handle */}
      {dragHandleProps && (
        <button
          className={cn(
            'p-0.5 -ml-1 cursor-grab active:cursor-grabbing',
            'text-neutral-400 hover:text-neutral-600',
            'dark:text-neutral-500 dark:hover:text-neutral-400',
            'focus:outline-none'
          )}
          {...dragHandleProps.listeners}
          {...dragHandleProps.attributes}
          aria-label={t('tools.dragToSort')}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}

      <Icon className="h-5 w-5 text-neutral-500 dark:text-neutral-400" />
      <span className="flex-1 text-sm font-medium text-neutral-900 dark:text-neutral-100">
        {tool.name}
      </span>

      {/* Favorite Toggle */}
      <button
        onClick={onToggleFavorite}
        disabled={disabled}
        className={cn(
          'p-1.5 rounded-md transition-colors',
          'hover:bg-neutral-200 dark:hover:bg-neutral-700',
          'focus:outline-none focus:ring-2 focus:ring-primary-500',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
        title={isFavorite ? t('common.removeFromFavorites') : t('tools.addToFavorites')}
        aria-label={isFavorite ? t('common.removeFromFavorites') : t('tools.addToFavorites')}
      >
        <Star
          className={cn(
            'h-4 w-4',
            isFavorite
              ? 'fill-amber-400 text-amber-400'
              : 'text-neutral-400 dark:text-neutral-500'
          )}
        />
      </button>

      {/* Visibility Toggle */}
      <button
        onClick={onToggleVisibility}
        disabled={disabled}
        className={cn(
          'p-1.5 rounded-md transition-colors',
          'hover:bg-neutral-200 dark:hover:bg-neutral-700',
          'focus:outline-none focus:ring-2 focus:ring-primary-500',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
        title={isHidden ? t('common.showTool') : t('common.hideTool')}
        aria-label={isHidden ? t('common.showTool') : t('common.hideTool')}
      >
        {isHidden ? (
          <EyeOff className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />
        ) : (
          <Eye className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />
        )}
      </button>
    </div>
  );
}
